import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useRecalls } from '@hooks/useRegulatory';
import type {
  RecallItem,
  RecallFilters,
  RecallReason,
  RecallUrgency,
  RecallStatus,
} from '@/types/regulatory';
import { MD_RECALL_REASON_LABEL_MAP } from './recallLabels';
import CreateRecallModal from './CreateRecallModal';

const MD_STATUS_OPTIONS: { value: RecallStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Draft', label: 'Чернетка' },
  { value: 'Active', label: 'Активний' },
  { value: 'Completed', label: 'Завершений' },
  { value: 'Cancelled', label: 'Скасований' },
];

const MD_STATUS_COLOR_MAP: Record<RecallStatus, string> = {
  Draft: 'default',
  Active: 'red',
  Completed: 'green',
  Cancelled: 'default',
};

const MD_STATUS_LABEL_MAP: Record<RecallStatus, string> = {
  Draft: 'Чернетка',
  Active: 'Активний',
  Completed: 'Завершений',
  Cancelled: 'Скасований',
};

const MD_URGENCY_COLOR_MAP: Record<RecallUrgency, string> = {
  Voluntary: 'default',
  Class1: 'red',
  Class2: 'orange',
  Class3: 'blue',
};

const MD_URGENCY_LABEL_MAP: Record<RecallUrgency, string> = {
  Voluntary: 'Добровільний',
  Class1: 'Клас I',
  Class2: 'Клас II',
  Class3: 'Клас III',
};

interface RecallsPageProps {
  onViewRecall: (mdRecallId: string) => void;
  // Викликається після створення — щоб одразу відкрити картку нового recall
  onRecallCreated: (mdRecallId: string) => void;
}

export default function RecallsPage({
  onViewRecall,
  onRecallCreated,
}: RecallsPageProps) {
  const [mdCreateOpen, setMdCreateOpen] = useState(false);
  const [mdSearch, setMdSearch] = useState('');
  const [mdStatusFilter, setMdStatusFilter] = useState<RecallStatus | ''>('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);

  const mdFilters: RecallFilters = useMemo(() => ({
    search: mdSearch || undefined,
    status: mdStatusFilter || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdStatusFilter, mdPage, mdPageSize]);

  const { data: mdRecallsData, isLoading: mdIsLoading } = useRecalls(mdFilters);

  const mdColumns: ColumnsType<RecallItem> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 140,
      sorter: (mdA, mdB) => mdA.number.localeCompare(mdB.number),
    },
    {
      title: 'Продукт',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Причина',
      dataIndex: 'recallReason',
      key: 'recallReason',
      ellipsis: true,
      render: (mdValue: RecallReason) => MD_RECALL_REASON_LABEL_MAP[mdValue] ?? mdValue,
    },
    {
      title: 'Терміновість',
      dataIndex: 'recallUrgency',
      key: 'recallUrgency',
      width: 130,
      render: (mdValue: RecallUrgency) => (
        <Tag color={MD_URGENCY_COLOR_MAP[mdValue]}>
          {MD_URGENCY_LABEL_MAP[mdValue] ?? mdValue}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (mdValue: RecallStatus) => (
        <Tag color={MD_STATUS_COLOR_MAP[mdValue]}>
          {MD_STATUS_LABEL_MAP[mdValue]}
        </Tag>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'initiatedDate',
      key: 'initiatedDate',
      width: 120,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
    },
    {
      title: 'К-ть партій',
      dataIndex: 'affectedBatchCount',
      key: 'affectedBatchCount',
      width: 110,
      align: 'right',
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Recalls
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setMdCreateOpen(true)}>
          Новий recall
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
      </Space>

      <Table<RecallItem>
        columns={mdColumns}
        dataSource={mdRecallsData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => onViewRecall(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdRecallsData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <CreateRecallModal
        open={mdCreateOpen}
        onClose={() => setMdCreateOpen(false)}
        onCreated={(mdRecallId) => {
          setMdCreateOpen(false);
          onRecallCreated(mdRecallId);
        }}
      />
    </>
  );
}
